import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ShieldCheck, Quote, Clock, Zap, BadgeCheck, Cpu, Briefcase, Palette, HeartHandshake, Star, Users, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { LineReveal, Reveal, FadeIn } from "@/components/Reveal";
import CourseCard from "@/components/CourseCard";
import Marquee from "@/components/Marquee";

const MANIFESTO = [
  { n: "01", title: "Practitioners, not trainers", body: "Every instructor built a career doing the exact work they teach. Not someone who tried it briefly. Not someone who went straight into training. They performed at a real, professional level first." },
  { n: "02", title: "Verified before they teach", body: "That track record is checked before onboarding, and stays visible on every course page as the roster grows." },
  { n: "03", title: "A conversation, not a checkout", body: "Every fee is a starting figure. You express interest; the exact amount and dates are confirmed with a real person. No pressure, no countdown timers." },
];

// Pastel icon-badge category tiles, eduhive style — flat color background,
// white rounded icon chip, no photo. Avoids dark-photo tiles entirely.
const CATEGORY_STYLE = {
  Technical: { bg: "bg-skylight", icon: Cpu, iconBg: "bg-sky" },
  Business: { bg: "bg-lemonlight", icon: Briefcase, iconBg: "bg-lemon" },
  Creative: { bg: "bg-roselight", icon: Palette, iconBg: "bg-rose" },
  "Life Skills": { bg: "bg-tealight", icon: HeartHandshake, iconBg: "bg-teal" },
};

export default function Home() {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/courses").then(({ data }) => { setCourses(data.courses); setCategories(data.categories); }).catch(() => {});
  }, []);

  const filteredCourses = useMemo(
    () => (filter === "All" ? courses : courses.filter((c) => c.category === filter)).slice(0, 6),
    [courses, filter]
  );

  const instructors = useMemo(() => {
    const seen = new Set();
    return courses.filter((c) => {
      if (seen.has(c.instructor.name)) return false;
      seen.add(c.instructor.name);
      return true;
    }).slice(0, 4);
  }, [courses]);

  // Flatten every course's dated events into one list for the "Upcoming" section.
  const upcoming = useMemo(() => {
    const rows = [];
    courses.forEach((c) => {
      (c.events || []).forEach((ev) => rows.push({ ...ev, course: c }));
    });
    return rows;
  }, [courses]);

  return (
    <div data-testid="home-page">
      {/* HERO — split layout, overlapping image collage, floating verified badge */}
      <section className="mesh-wash overflow-hidden">
        <div className="blob-decoration w-[420px] h-[420px] bg-violetlight -top-32 -left-32" />
        <div className="blob-decoration w-[360px] h-[360px] bg-lemonlight top-10 -right-24" />
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pt-12 md:pt-20 pb-20 md:pb-28 items-center">
            <div className="lg:col-span-6">
              <FadeIn>
                <span className="inline-flex items-center gap-2 rounded-full bg-emeraldlight border border-emerald/10 px-4 py-2 font-sans text-xs font-bold text-emerald mb-8">
                  <ShieldCheck size={14} /> The pathway to real skill
                </span>
              </FadeIn>
              <h1 className="font-display font-bold tracking-tight text-5xl sm:text-6xl lg:text-[4.6rem] lg:leading-[1.0] text-ink">
                <LineReveal lines={["Learn it from", "someone who's"]} />
                <span className="block">
                  <LineReveal lines={["actually"]} className="inline-block" delay={0.34} />{" "}
                  <span className="relative inline-block text-terracotta">
                    done it.
                    <svg className="absolute left-0 -bottom-2 w-full" height="12" viewBox="0 0 200 12" preserveAspectRatio="none" aria-hidden="true">
                      <path d="M2 9 C 60 2, 140 2, 198 9" stroke="#FFC53D" strokeWidth="6" fill="none" strokeLinecap="round" />
                    </svg>
                  </span>
                </span>
              </h1>
              <FadeIn delay={0.6}>
                <p className="font-sans text-lg md:text-xl text-graphite mt-8 max-w-lg leading-relaxed">
                  Not a professional trainer who never did the work — a practitioner who
                  built a career in it, now teaching what they lived.
                </p>
              </FadeIn>
              <FadeIn delay={0.75}>
                <div className="mt-9 flex flex-wrap items-center gap-5">
                  <Link to="/courses" data-testid="hero-find-course" className="group inline-flex items-center gap-2.5 rounded-full bg-terracotta text-white font-bold text-base px-8 py-4 shadow-md hover:bg-terracottahover hover:shadow-glowwarm hover:scale-[1.02] transition duration-300">
                    Find a course <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link to="/about" data-testid="hero-about" className="inline-flex items-center gap-2 font-sans font-bold text-ink hover:text-emerald transition-colors">
                    About us <ArrowRight size={16} />
                  </Link>
                </div>
              </FadeIn>
            </div>

            <div className="lg:col-span-6">
              <div className="relative h-[440px] md:h-[500px] flex items-center justify-center">
                {/* solid color blob behind the photo, eduhive "About" style */}
                <div className="absolute inset-0 gradient-brand blob-shape opacity-90" />
                {/* organic blob-cropped photo */}
                <div className="absolute inset-6 md:inset-10 blob-shape-alt overflow-hidden">
                  <img
                    src="https://images.pexels.com/photos/7108207/pexels-photo-7108207.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                    alt="Learners with a real practitioner"
                    className="h-full w-full object-cover"
                  />
                </div>
                {/* floating verified badge */}
                <div className="absolute top-2 left-0 md:left-2 bg-surface rounded-2xl shadow-md p-4 flex items-center gap-3 max-w-[210px]">
                  <span className="shrink-0 w-11 h-11 rounded-full bg-emerald text-white flex items-center justify-center">
                    <BadgeCheck size={20} />
                  </span>
                  <div>
                    <p className="font-display font-bold text-ink text-sm leading-tight">100% verified</p>
                    <p className="font-sans text-xs text-graphite leading-tight mt-0.5">Every instructor, checked</p>
                  </div>
                </div>
                <div className="absolute bottom-4 right-0 md:right-2 bg-surface rounded-2xl shadow-md p-4 flex items-center gap-3 max-w-[190px]">
                  <span className="shrink-0 w-11 h-11 rounded-full bg-terracotta text-white flex items-center justify-center">
                    <Users size={20} />
                  </span>
                  <div>
                    <p className="font-display font-bold text-ink text-sm leading-tight">4 fields</p>
                    <p className="font-sans text-xs text-graphite leading-tight mt-0.5">Of real skill</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY SHOWCASE — full-bleed image tiles */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <Reveal>
          <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">Explore by discipline</p>
          <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-tight text-ink max-w-2xl">Any serious skill belongs here.</h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {categories.map((cat, i) => {
            const count = courses.filter((c) => c.category === cat).length;
            const style = CATEGORY_STYLE[cat] || { bg: "bg-violetlight", icon: Star, iconBg: "bg-violet" };
            const Icon = style.icon;
            return (
              <Reveal key={cat} delay={i * 0.06}>
                <Link
                  to={`/courses?cat=${encodeURIComponent(cat)}`}
                  data-testid={`home-field-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`group block rounded-3xl p-7 shadow-sm hover:shadow-lg hover:-translate-y-1.5 transition duration-300 ${style.bg}`}
                >
                  <span className={`inline-flex w-16 h-16 rounded-2xl items-center justify-center shadow-sm bg-surface`}>
                    <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${style.iconBg}`}>
                      <Icon size={20} className="text-white" />
                    </span>
                  </span>
                  <h3 className="font-display font-semibold text-xl leading-tight mt-6 text-ink">{cat}</h3>
                  <p className="font-sans text-sm text-graphite mt-1">{count} course{count !== 1 ? "s" : ""}</p>
                  <span className="mt-4 inline-flex items-center gap-1 font-sans text-sm font-bold text-ink/70 group-hover:text-emerald transition-colors">
                    Explore <ArrowUpRight size={14} />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <Marquee items={["Cybersecurity", "Web Development", "Performance Marketing", "B2B Sales", "Public Speaking", "Contemporary Dance", "Freelancing", "Fitness Coaching"]} />

      {/* COURSES GRID — filter pills, like reference course sections */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <Reveal>
          <div className="flex flex-col items-center text-center">
            <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">Our courses</p>
            <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-tight text-ink">Our most popular courses.</h2>
          </div>
        </Reveal>
        <div className="mt-8 flex flex-wrap justify-center gap-2.5">
          {["All", ...categories].map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`home-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-sans text-sm font-bold rounded-full px-5 py-2.5 transition-colors ${
                filter === cat ? "bg-emerald text-white" : "bg-stone text-graphite hover:bg-emeraldlight hover:text-emerald"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        <motion.div
          key={filter}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {filteredCourses.map((c, i) => (
            <CourseCard key={c.slug} course={c} index={i} />
          ))}
        </motion.div>
        <div className="mt-10 text-center">
          <Link to="/courses" data-testid="home-see-all" className="inline-flex items-center gap-2 font-sans font-bold text-emerald hover:text-terracotta transition-colors">
            See the full catalog <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* UPCOMING — dated batches and demo sessions across all courses */}
      {upcoming.length > 0 && (
        <section className="bg-stone py-14 md:py-20" data-testid="home-upcoming">
          <div className="w-full mx-auto px-4 md:px-8 lg:px-12">
            <Reveal>
              <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">Upcoming</p>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-tight text-ink max-w-2xl">
                Batches and demo sessions coming up.
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {upcoming.map((ev, i) => {
                const isDemo = ev.kind === "demo";
                return (
                  <Reveal key={`${ev.course.slug}-${i}`} delay={(i % 2) * 0.06}>
                    <Link
                      to={`/courses/${ev.course.slug}`}
                      data-testid={`home-upcoming-${ev.course.slug}-${ev.kind}`}
                      className={`group block rounded-2xl border bg-surface p-6 transition-colors ${
                        isDemo ? "border-terracotta/40 hover:border-terracotta" : "border-line hover:border-emerald"
                      }`}
                    >
                      <div className={`flex items-center gap-2 ${isDemo ? "text-terracotta" : "text-emerald"}`}>
                        {isDemo ? <Zap size={16} /> : <CalendarClock size={16} />}
                        <span className="font-sans text-xs font-bold uppercase tracking-wide">
                          {ev.title} · {ev.course.title}
                        </span>
                        {ev.fee && (
                          <span className="ml-auto font-sans text-xs font-bold text-terracotta bg-white rounded-full px-2.5 py-1 border border-terracotta/30">
                            {ev.fee}
                          </span>
                        )}
                      </div>
                      <p className="font-display font-bold text-xl md:text-2xl text-ink mt-3 leading-tight">
                        {ev.date_label}
                      </p>
                      {ev.note && (
                        <p className="font-sans text-[15px] text-graphite mt-2 leading-relaxed line-clamp-2">{ev.note}</p>
                      )}
                      <span className="mt-4 inline-flex items-center gap-1.5 font-sans text-sm font-bold text-emerald group-hover:text-terracotta transition-colors">
                        Details &amp; register <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
            <p className="font-sans text-xs font-semibold text-graphite mt-6">
              No payment on this site. Dates and any demo payment are confirmed with you directly.
            </p>
          </div>
        </section>
      )}

      {/* MANIFESTO — colorful bento */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <Reveal>
          <p className="font-sans text-xs font-bold text-terracotta mb-4 uppercase tracking-wide">What we stand for</p>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-6xl max-w-3xl leading-[1.02] text-ink">
            The one thing every course here has in common.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-7">
            <div className="h-full gradient-brand text-white rounded-3xl p-10 md:p-12 flex flex-col justify-between min-h-[300px] relative overflow-hidden">
              <div className="blob-decoration w-56 h-56 bg-white/10 -top-16 -right-16" />
              <span className="font-display font-bold text-7xl text-lemon leading-none relative">{MANIFESTO[0].n}</span>
              <div className="relative">
                <h3 className="font-display font-semibold text-3xl md:text-4xl mt-6">{MANIFESTO[0].title}</h3>
                <p className="text-white/80 mt-4 leading-relaxed max-w-md text-lg">{MANIFESTO[0].body}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <div className="h-full bg-terracotta text-white rounded-3xl p-10 flex flex-col justify-between min-h-[300px]">
              <span className="font-display font-bold text-7xl text-white/40 leading-none">{MANIFESTO[1].n}</span>
              <div>
                <h3 className="font-display font-semibold text-3xl mt-6">{MANIFESTO[1].title}</h3>
                <p className="text-white/85 mt-4 leading-relaxed text-lg">{MANIFESTO[1].body}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-12">
            <div className="bg-violetlight border border-violet/10 rounded-3xl p-10 md:p-12 flex flex-col md:flex-row md:items-center gap-6">
              <span className="font-display font-bold text-7xl text-violet leading-none">{MANIFESTO[2].n}</span>
              <div className="md:flex-1">
                <h3 className="font-display font-semibold text-3xl text-ink">{MANIFESTO[2].title}</h3>
                <p className="text-graphite mt-3 leading-relaxed text-lg max-w-2xl">{MANIFESTO[2].body}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INSTRUCTOR SPOTLIGHT — grid, like reference instructor section */}
      {instructors.length > 0 && (
        <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
          <Reveal>
            <div className="flex flex-col items-center text-center">
              <p className="font-sans text-xs font-bold text-terracotta mb-3 uppercase tracking-wide">Meet the practitioners</p>
              <h2 className="font-display font-bold tracking-tight text-3xl md:text-5xl leading-tight text-ink max-w-2xl">
                Every one of them still does the work.
              </h2>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {instructors.map((c, i) => (
              <Reveal key={c.instructor.name} delay={i * 0.06}>
                <Link to={`/courses/${c.slug}`} className="group block rounded-3xl overflow-hidden bg-surface border border-line shadow-sm hover:shadow-lg hover:-translate-y-1 transition duration-300">
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img src={c.image} alt={c.instructor.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-surface/95 text-emerald font-sans text-[11px] font-bold px-2.5 py-1.5 rounded-full shadow-sm">
                      <ShieldCheck size={12} /> Verified
                    </span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-semibold text-lg leading-tight group-hover:text-emerald transition-colors">{c.instructor.name}</h3>
                    <p className="font-sans text-xs font-semibold text-graphite mt-1 leading-snug">{c.instructor.role}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* STATS — floating cards over a color blob, eduhive style. Only real, honest counts. */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="relative rounded-[3rem] gradient-brand overflow-hidden px-8 md:px-16 py-16 md:py-20">
          <div className="topo-texture absolute inset-0 opacity-40" style={{ filter: "invert(1) brightness(2)" }} />
          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
            <StatCard icon={<Users size={22} />} value={String(courses.length)} label="Live courses" />
            <StatCard icon={<ShieldCheck size={22} />} value="100%" label="Verified instructors" />
            <StatCard icon={<Star size={22} />} value={String(categories.length)} label="Fields of skill" />
            <StatCard icon={<Zap size={22} />} value="0" label="Fabricated reviews" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — honest, editorial */}
      <section className="relative bg-stone topo-texture overflow-hidden">
        <Quote className="absolute -top-6 left-4 md:left-16 text-emerald/10 w-40 h-40 md:w-64 md:h-64" />
        <div className="w-full mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20 relative">
          <p className="font-sans text-xs font-bold text-terracotta mb-6 uppercase tracking-wide">In their words — real quotes only</p>
          <p className="font-serif italic text-3xl md:text-5xl leading-[1.15] max-w-4xl text-ink/80">
            We'd rather leave this space honest than fill it. Verified reviews from real
            students appear here as our first batches finish — never invented, never
            borrowed.
          </p>
          <p className="font-sans text-graphite mt-8 max-w-2xl text-lg">
            Two or three true words will always outweigh a wall of generic ones. That's the
            same promise that governs who is allowed to teach here.
          </p>
        </div>
      </section>

      {/* CLOSING CTA — pale tint, color reserved for accents */}
      <section className="w-full mx-auto px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <Reveal>
          <div className="bg-stone rounded-3xl px-8 md:px-16 py-10 md:py-14 relative overflow-hidden">
            <div className="blob-decoration w-96 h-96 bg-tealight -bottom-32 -right-20" />
            <p className="font-sans text-xs font-bold text-terracotta mb-6 relative uppercase tracking-wide">
              Every fee is a starting figure — confirmed in conversation
            </p>
            <h2 className="font-display font-bold tracking-tight text-4xl md:text-7xl leading-[1.0] max-w-4xl relative text-ink">
              Find your teacher. <span className="text-emerald">Enquire</span> when you're ready.
            </h2>
            <div className="mt-10 relative">
              <Link to="/courses" data-testid="closing-cta" className="group inline-flex items-center gap-2.5 rounded-full bg-terracotta text-white font-bold text-base px-10 py-5 shadow-md hover:bg-terracottahover hover:shadow-glowwarm hover:scale-[1.02] transition duration-300">
                Explore all courses <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function StatCard({ icon, value, label }) {
  return (
    <div className="bg-surface/95 backdrop-blur rounded-2xl p-5 md:p-6 shadow-md text-center">
      <span className="inline-flex w-11 h-11 rounded-full bg-emeraldlight text-emerald items-center justify-center">{icon}</span>
      <p className="font-display font-bold text-2xl md:text-3xl text-ink mt-3">{value}</p>
      <p className="font-sans text-xs font-semibold text-graphite mt-1 leading-snug">{label}</p>
    </div>
  );
}
